import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import config from '../config';
import { PASSWORD_RULES, validatePassword } from '../utils/validatePassword';

function PolicyChecker({ password }) {
  return (
    <ul className="space-y-1 mt-2">
      {PASSWORD_RULES.map(r => {
        const passed = r.test(password);
        return (
          <li key={r.label} className={`flex items-center gap-2 text-xs ${passed ? 'text-green-600' : 'text-gray-400'}`}>
            <span>{passed ? '\u2713' : '\u25cb'}</span>
            {r.label}
          </li>
        );
      })}
    </ul>
  );
}

export default function ResetPassword() {
  const navigate = useNavigate();
  const params = new URLSearchParams(window.location.search);
  const token = params.get('token');

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  if (!token) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="bg-white rounded-xl shadow p-8 max-w-md w-full text-center">
          <div className="text-4xl mb-4">{'\u26a0\ufe0f'}</div>
          <h2 className="text-xl font-bold text-gray-800 mb-2">Invalid Link</h2>
          <p className="text-gray-500">This reset link is invalid. Please request a new one.</p>
          <button onClick={() => navigate('/forgot-password')} className="mt-4 text-blue-600 text-sm hover:underline">
            Request New Link
          </button>
        </div>
      </div>
    );
  }

  const allRulesPassed = validatePassword(password);

  const handleSubmit = async () => {
    setError('');
    if (!allRulesPassed) {
      setError('Password does not meet the security requirements.');
      return;
    }
    if (password !== confirm) {
      setError('Passwords do not match.');
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${config.API_BASE_URL}/auth/reset-password`, { token, password });
      setDone(true);
      setTimeout(() => navigate('/login'), 3000);
    } catch (err) {
      setError(err?.response?.data?.detail || 'Something went wrong. The link may have expired.');
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="bg-white rounded-xl shadow p-8 max-w-md w-full text-center">
          <div className="text-4xl mb-4">{'\u2705'}</div>
          <h2 className="text-xl font-bold text-gray-800 mb-2">Password Reset!</h2>
          <p className="text-gray-500">Redirecting you to login...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="bg-white rounded-xl shadow p-8 max-w-md w-full">
        <div className="mb-6 text-center">
          <div className="text-4xl mb-3">🔐</div>
          <h2 className="text-2xl font-bold text-gray-900">Reset Your Password</h2>
          <p className="text-gray-500 mt-1 text-sm">Choose a new strong password for your account.</p>
        </div>

        {error && (
          <div className="mb-4 bg-red-50 border border-red-200 text-red-700 rounded-lg px-4 py-3 text-sm">{error}</div>
        )}

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">New Password</label>
            <input
              type="password"
              value={password}
              onChange={e => setPassword(e.target.value)}
              placeholder="Enter a strong password"
              className="w-full border border-gray-300 rounded-lg px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            {password.length > 0 && <PolicyChecker password={password} />}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Confirm Password</label>
            <input
              type="password"
              value={confirm}
              onChange={e => setConfirm(e.target.value)}
              placeholder="Re-enter your password"
              className="w-full border border-gray-300 rounded-lg px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            {confirm.length > 0 && password !== confirm && (
              <p className="text-xs text-red-500 mt-1">Passwords do not match.</p>
            )}
            {confirm.length > 0 && password === confirm && (
              <p className="text-xs text-green-600 mt-1">{'\u2713'} Passwords match.</p>
            )}
          </div>
          <button
            onClick={handleSubmit}
            disabled={loading || !allRulesPassed || password !== confirm}
            className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2 px-4 rounded-lg text-sm transition disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? 'Resetting...' : 'Reset Password'}
          </button>
          <div className="text-center">
            <button onClick={() => navigate('/login')} className="text-blue-600 text-sm hover:underline">
              Back to Login
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
